import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Banknote, Landmark, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

const STATUS = [
  { v: 'carteira', label: 'Em carteira', cor: 'bg-amber-100 text-amber-800' },
  { v: 'depositado', label: 'Depositados', cor: 'bg-blue-100 text-blue-800' },
  { v: 'compensado', label: 'Compensados', cor: 'bg-emerald-100 text-emerald-800' },
  { v: 'devolvido', label: 'Devolvidos', cor: 'bg-rose-100 text-rose-800' },
]

export default function ChequesCarteiraPage() {
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState<string>('carteira')
  const [contaDep, setContaDep] = useState<string>('')

  const { data: cheques = [], isLoading } = useQuery<any[]>({
    queryKey: ['cheques', filtro],
    queryFn: async () => (await api.get('/financeiro/cheques', { params: filtro ? { status: filtro } : {} })).data,
  })
  const { data: contas = [] } = useQuery<any[]>({
    queryKey: ['contas-bancarias'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })

  const inval = () => qc.invalidateQueries({ queryKey: ['cheques'] })
  const depositar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/financeiro/cheques/${id}/depositar`, { conta_id: Number(contaDep) })).data,
    onSuccess: () => { toast.success('Cheque depositado'); inval() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const compensar = useMutation({
    mutationFn: async (id: number) => (await api.post(`/financeiro/cheques/${id}/compensar`)).data,
    onSuccess: () => { toast.success('Cheque compensado — entrou no banco'); inval() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const devolver = useMutation({
    mutationFn: async (id: number) => (await api.post(`/financeiro/cheques/${id}/devolver`)).data,
    onSuccess: () => { toast.success('Cheque marcado devolvido'); inval() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const total = cheques.reduce((s, c) => s + (c.valor || 0), 0)

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold flex items-center gap-2"><Banknote size={22} /> Carteira de Cheques</h1>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS.map((s) => (
          <button key={s.v} onClick={() => setFiltro(s.v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtro === s.v ? 'bg-brand-600 text-white' : 'border border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
            {s.label}
          </button>
        ))}
        <button onClick={() => setFiltro('')} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtro === '' ? 'bg-brand-600 text-white' : 'border border-ink-200 text-ink-600 hover:bg-ink-50'}`}>Todos</button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-ink-500">Conta p/ depósito:</span>
          <select value={contaDep} onChange={(e) => setContaDep(e.target.value)} className="input !py-1 text-sm">
            <option value="">Selecione…</option>
            {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>

      <div className="text-sm text-ink-600">Total: <strong>{formatMoney(total)}</strong> · {cheques.length} cheque(s)</div>

      {isLoading ? <div className="p-8 text-center text-ink-500">Carregando…</div> : cheques.length === 0 ? (
        <div className="card card-body text-center text-ink-500">Nenhum cheque.</div>
      ) : (
        <div className="table-wrap">
          <table className="table text-sm">
            <thead><tr><th>Cliente</th><th>Banco</th><th>Nº</th><th className="text-right">Valor</th><th>Bom para</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
            <tbody>
              {cheques.map((c) => {
                const meta = STATUS.find((s) => s.v === c.status) || { label: c.status, cor: 'bg-ink-100 text-ink-600' }
                const busy = depositar.isPending || compensar.isPending || devolver.isPending
                return (
                  <tr key={c.id}>
                    <td>{c.pessoa || '—'}</td>
                    <td>{c.banco || '—'}</td>
                    <td>{c.numero || '—'}</td>
                    <td className="text-right font-mono">{formatMoney(c.valor)}</td>
                    <td>{c.bom_para ? new Date(c.bom_para).toLocaleDateString('pt-BR') : '—'}</td>
                    <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.cor}`}>{meta.label}</span></td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {c.status === 'carteira' && (
                          <button onClick={() => contaDep ? depositar.mutate(c.id) : toast.error('Selecione a conta de depósito acima')} disabled={busy}
                            className="flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs disabled:opacity-50">
                            <Landmark size={13} /> Depositar
                          </button>
                        )}
                        {c.status === 'depositado' && (
                          <button onClick={() => compensar.mutate(c.id)} disabled={busy}
                            className="flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-50">
                            {compensar.isPending ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} Compensar
                          </button>
                        )}
                        {['carteira', 'depositado'].includes(c.status) && (
                          <button onClick={() => { if (confirm('Marcar cheque como devolvido?')) devolver.mutate(c.id) }} disabled={busy}
                            className="flex items-center gap-1 px-2.5 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs disabled:opacity-50">
                            <XCircle size={13} /> Devolver
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
